import { createSelector } from '@reduxjs/toolkit';
import { Store } from 'components/common/types/Store.types';
import { ProductTemplate } from 'components/common/types/DirectSalesProductTemplate.types';
import { DirectSalesLine, DirectSalesPricingMode } from 'components/common/types/DirectSalesCampaign.types';
import { Line } from 'components/common/types/Deal.types';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { getHasDealReadOnlyPermission } from 'store/dealManagement/selectors/getHasDealReadOnlyPermission';
import { NewProduct, ProductLine, SelectionPermission } from './reducer.types';
import { createProductLine } from './reducer';

export const getSelectedProductTemplateRules = createSelector(
  [
    (state: Store) => state.productManagement.selectedProducts,
    (state: Store) => state.dealManagement.backupFormData.lines,
    (_state: Store, lineId: string) => lineId,
  ],
  (selectedProductTemplates, lines, lineId) => {
    const currentLine = lines.find((line: Line | DirectSalesLine) => line.lineId === lineId);

    if (
      !currentLine ||
      !('productTemplate' in currentLine) ||
      !currentLine.productTemplate?.id ||
      currentLine.productLineReference === undefined
    ) {
      return [];
    }

    const product = selectedProductTemplates?.find(
      (template: ProductTemplate) =>
        template.productTemplate?.id === currentLine.productTemplate?.id &&
        template.productLineReference === currentLine.productLineReference,
    );

    return product?.rules ?? [];
  },
);

export const getActiveProductLine = createSelector(
  [(state: Store) => state.productManagement.productLines, (state: Store) => state.productManagement.activeLineId],
  (productLines, activeLineId): ProductLine => {
    const activeLine = productLines.find((line: ProductLine) => line.lineId === activeLineId);
    return activeLine ?? createProductLine({ lineId: '', name: 'Line 1' });
  },
);

export const getIsPricingMode = createSelector(
  [(state: Store) => state.productManagement.newProduct.pricingMode],
  (pricingMode): boolean => {
    return pricingMode !== undefined;
  },
);

export const getIsSchedule = createSelector(
  [(state: Store) => state.productManagement.newProduct.range],
  (range): boolean => {
    return range !== undefined;
  },
);

export const getIsMediaType = createSelector(
  [(state: Store) => state.productManagement.newProduct.mediaType],
  (mediaType): boolean => {
    return mediaType !== undefined;
  },
);

export const getIsEnvironment = createSelector(
  [(state: Store) => state.productManagement.newProduct.channels],
  (channels): boolean => {
    return channels !== undefined;
  },
);

export const getIsTargets = createSelector(
  [
    (state: Store) => state.productManagement.newProduct.budget,
    (state: Store) => state.productManagement.newProduct.frames,
    (state: Store) => state.productManagement.newProduct.impressions,
  ],
  (budget, frames, impressions): boolean => {
    return budget !== undefined && frames !== undefined && impressions !== undefined;
  },
);

export const getIsNetworks = createSelector(
  [(state: Store) => state.productManagement.newProduct.networks],
  (networks): boolean => {
    return networks !== undefined;
  },
);

export const getIsNetworksEnabled = createSelector(
  [
    (state: Store) => state.productManagement.newProduct.mediaType,
    (state: Store) => state.productManagement.newProduct.channels,
    (state: Store) => state.productManagement.newProduct.pricingMode?.value,
  ],
  (mediaType, channels, pricingMode): boolean => {
    if (pricingMode === DirectSalesPricingMode.CPM || pricingMode === DirectSalesPricingMode.PANEL) {
      return false;
    }

    return mediaType !== undefined && channels !== undefined && pricingMode !== undefined;
  },
);

const getIsTargetsRequired = createSelector(
  (state: Store) => state.productManagement.newProduct.networks,
  (networks): boolean => {
    return networks === undefined || networks.selectionPermission === SelectionPermission.DISABLED;
  },
);

const getIsNetworksRequired = createSelector(
  [
    (state: Store) => state.productManagement.newProduct.budget,
    (state: Store) => state.productManagement.newProduct.frames,
    (state: Store) => state.productManagement.newProduct.impressions,
  ],
  (budget, frames, impressions): boolean => {
    return !budget && !frames && !impressions;
  },
);

interface RequiredProductSecondaryPanel {
  type: PlannerSecondaryPanelType;
  getIsFilled: (state: Store) => boolean;
  getIsEnabled?: (state: Store) => boolean;
  getIsRequired?: (state: Store) => boolean;
}

export const REQUIRED_PCM_PANELS: RequiredProductSecondaryPanel[] = [
  {
    type: PlannerSecondaryPanelType.PRICING_MODE,
    getIsFilled: getIsPricingMode,
    getIsEnabled: () => true,
    getIsRequired: () => true,
  },
  {
    type: PlannerSecondaryPanelType.SCHEDULE,
    getIsFilled: getIsSchedule,
    getIsEnabled: () => true,
    getIsRequired: () => true,
  },
  {
    type: PlannerSecondaryPanelType.MEDIA_TYPE,
    getIsFilled: getIsMediaType,
    getIsEnabled: () => true,
    getIsRequired: () => true,
  },
  {
    type: PlannerSecondaryPanelType.CHANNEL,
    getIsFilled: getIsEnvironment,
    getIsEnabled: () => true,
    getIsRequired: () => true,
  },
  {
    type: PlannerSecondaryPanelType.TARGETS,
    getIsFilled: getIsTargets,
    getIsEnabled: () => true,
    getIsRequired: getIsTargetsRequired,
  },
  {
    type: PlannerSecondaryPanelType.NETWORKS,
    getIsFilled: getIsNetworks,
    getIsEnabled: getIsNetworksEnabled,
    getIsRequired: getIsNetworksRequired,
  },
];

export const getRequiredProductSecondaryPanel = createSelector(
  (state: Store) => state.productManagement.secondaryPanel,
  (secondaryPanel): RequiredProductSecondaryPanel | undefined => {
    return REQUIRED_PCM_PANELS.find((panel) => panel.type === secondaryPanel);
  },
);

export const getIsCurrentProductSecondaryPanelRequired = createSelector(
  getRequiredProductSecondaryPanel,
  (state: Store) => state,
  (requiredProductSecondaryPanel, state): boolean => {
    return !!requiredProductSecondaryPanel && !!requiredProductSecondaryPanel.getIsRequired?.(state);
  },
);

export const getCurrentProductSecondaryPanel = createSelector(
  (state: Store) => state.productManagement.secondaryPanel,
  (secondaryPanel): PlannerSecondaryPanelType => {
    return secondaryPanel;
  },
);

export const getIsCurrentProductSecondaryPanelFilled = createSelector(
  getRequiredProductSecondaryPanel,
  (state: Store) => state,
  (requiredProductSecondaryPanel, state): boolean => {
    return !!requiredProductSecondaryPanel?.getIsFilled(state);
  },
);

export const getNextRequiredProductSecondaryPanel = createSelector(
  getRequiredProductSecondaryPanel,
  (state: Store) => state,
  (requiredProductSecondaryPanel, state): PlannerSecondaryPanelType => {
    const indexOfCurrentPanel = REQUIRED_PCM_PANELS.findIndex(
      (panel) => panel.type === requiredProductSecondaryPanel?.type,
    );

    if (indexOfCurrentPanel === -1) return PlannerSecondaryPanelType.NONE;

    const searchOrder = [
      ...REQUIRED_PCM_PANELS.slice(indexOfCurrentPanel + 1),
      ...REQUIRED_PCM_PANELS.slice(0, indexOfCurrentPanel),
    ];

    return (
      searchOrder.find((panel) => {
        return (panel.getIsEnabled?.(state) ?? true) && !!panel.getIsRequired?.(state) && !panel.getIsFilled(state);
      })?.type ?? PlannerSecondaryPanelType.NONE
    );
  },
);

export const getIsReadOnly = createSelector(
  (state: Store) => state.productManagement.isReadOnly,
  getHasDealReadOnlyPermission,
  (isReadOnly, hasDealReadOnlyPermission): boolean => {
    return isReadOnly || hasDealReadOnlyPermission;
  },
);

export const getNewProduct = createSelector(
  (state: Store) => state.productManagement.newProduct,
  (newProduct): NewProduct => newProduct,
);
